// Centralized API client for consistent error handling and request patterns
import { router } from "@inertiajs/react";

export interface ApiError extends Error {
  status?: number;
  response?: Response;
  data?: any;
}

export interface ApiResponse<T = any> {
  data: T;
  ok: boolean;
  status: number;
  statusText: string;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: any;
  headers?: Record<string, string>;
  timeout?: number;
}

class ApiClient {
  private defaultTimeout = 30000; // 30 seconds
  private baseHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  constructor() {
    // Get CSRF token from meta tag (Rails standard)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
    if (csrfToken) {
      this.baseHeaders["X-CSRF-Token"] = csrfToken;
    }
  }

  private async makeRequest<T = any>(
    url: string,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const { method = "GET", body, headers = {}, timeout = this.defaultTimeout } = options;

    // Merge headers
    const finalHeaders = { ...this.baseHeaders, ...headers };

    // Set up request options
    const fetchOptions: RequestInit = {
      method,
      headers: finalHeaders,
    };

    // Add body for non-GET requests
    if (body && method !== "GET") {
      fetchOptions.body = typeof body === "string" ? body : JSON.stringify(body);
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    fetchOptions.signal = controller.signal;

    try {
      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      // Parse response body
      let data: T;
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/json")) {
        data = await response.json();
      } else {
        data = (await response.text()) as unknown as T;
      }

      // Handle HTTP errors
      if (!response.ok) {
        const error: ApiError = new Error(
          `API request failed: ${response.status} ${response.statusText}`,
        );
        error.status = response.status;
        error.response = response;
        error.data = data;

        // Handle specific error cases
        if (response.status === 401) {
          // Unauthorized - redirect to login
          router.visit("/sign_in");
          throw error;
        } else if (response.status === 403) {
          // Forbidden - show error message
          throw new Error("Access denied. You do not have permission to perform this action.");
        } else if (response.status === 422) {
          // Validation errors - let component handle
          throw error;
        } else if (response.status >= 500) {
          // Server errors
          throw new Error("Server error. Please try again later.");
        }

        throw error;
      }

      return {
        data,
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === "AbortError") {
          throw new Error("Request timeout. Please try again.");
        }
        // Re-throw API errors as-is
        throw error;
      }

      // Unknown error
      throw new Error("An unexpected error occurred. Please try again.");
    }
  }

  // HTTP method helpers
  async get<T = any>(
    url: string,
    options: Omit<RequestOptions, "method"> = {},
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, { ...options, method: "GET" });
  }

  async post<T = any>(
    url: string,
    body?: any,
    options: Omit<RequestOptions, "method" | "body"> = {},
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, { ...options, method: "POST", body });
  }

  async put<T = any>(
    url: string,
    body?: any,
    options: Omit<RequestOptions, "method" | "body"> = {},
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, { ...options, method: "PUT", body });
  }

  async patch<T = any>(
    url: string,
    body?: any,
    options: Omit<RequestOptions, "method" | "body"> = {},
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, { ...options, method: "PATCH", body });
  }

  async delete<T = any>(
    url: string,
    options: Omit<RequestOptions, "method"> = {},
  ): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, { ...options, method: "DELETE" });
  }

  // Convenience methods for common patterns
  async getData<T = any>(url: string): Promise<T> {
    const response = await this.get<T>(url);
    return response.data;
  }

  async postData<T = any>(url: string, body?: any): Promise<T> {
    const response = await this.post<T>(url, body);
    return response.data;
  }

  async putData<T = any>(url: string, body?: any): Promise<T> {
    const response = await this.put<T>(url, body);
    return response.data;
  }

  async patchData<T = any>(url: string, body?: any): Promise<T> {
    const response = await this.patch<T>(url, body);
    return response.data;
  }

  async deleteData<T = any>(url: string): Promise<T> {
    const response = await this.delete<T>(url);
    return response.data;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export class for testing
export { ApiClient };

// Type definitions for common API responses
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
    per_page: number;
  };
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationErrorResponse {
  errors: ValidationError[];
  message: string;
}
